// @ts-check
import {EditImageOverlay} from '../EditImageOverlayView';
import {Graphic} from '../Graphic.js';
import {ImageOverlayView} from '../ImageOverlayView';
import * as EventHandlers from './EventHandlers';
import {SubscriptionManager} from '@/lib/filters/subscriptions';
import {MapService} from '@/services/MapService/Map.js';


/**
 * @typedef {object} ImageData
 * @property {string} [image] A base64 representation of the image to display.
 * @property {string} [imagePath] A path to a file on Google Cloud Storage containing the image to display.
 * @property {number} [rotation] The amount in degrees by which to rotate the image on the map.
 * @property {google.maps.LatLngBoundsLiteral} bounds The geographic bounds of the image.
 * @property {boolean} [preventScaling] Whether the image can be scaled when editing.
 *
 * @typedef {import('../GraphicService').GraphicData & ImageData} ImageGraphicData
 * @typedef {import('@/lib/filters/subscriptions.js').SubscriptionResult} SubscriptionResult
 * @typedef {import('@/lib/filters/subscriptions.js').SubscriptionEvent} SubscriptionEvent
 */

/**
 * @typedef Result
 * @property {"success"|"error"} type Indicates whether the result is a success or error.
 * @property {string} [name] If `type` is `"success"`, contains the name of the created overlay.
 * @property {string} [error] If `type` is `"error"`, contains the error message.
 */

class ImageGraphic extends Graphic {
  /**
   * @param {ImageGraphicData} data
   */
  constructor(data) {
    super();
    const {north, east, south, west} = data.bounds;
    if (!data.image) {
      throw new Error('Error constructing ImageGraphic: the image must be supplied.');
    }
    if (!north || !east || !south || !west) {
      throw new Error(`Error constructing ImageGraphic: the graphic's north, east, south, and west bounds must be supplied.`);
    }

    // database-persistent properties
    /** @type {string} */
    this.imagePath = data.imagePath;
    /** @type {number} */
    this.rotation = data.rotation ?? 0;
    /** @type {boolean} */
    this.preventScaling = data.preventScaling ?? false;
    /** @type {google.maps.LatLngBoundsLiteral} */
    this.bounds = data.bounds;

    // non-persistent properties
    /** @type {string} */
    this.image = data.image;
    /** @type {string} */
    this.id = this.uuid;
    /** @type {ImageOverlayView} */
    this.overlayView = new ImageOverlayView({
      image: this.image,
      bounds: this.bounds,
      rotation: this.rotation,
      id: this.id,
      imagePath: this.imagePath,
    });
    /** @type {EditImageOverlay} */
    this.editImageOverlay = null;
    /** @type {SubscriptionManager} */
    this.subscriptions = new SubscriptionManager();
    /** @type {boolean} */
    this._clickable = true;
  }

  addLocalHandler(eventType, handlerName, handler) {
    // The OverlayViewGraphic does not care about the handlerName.
    this.overlayView.clickDiv.addEventListener(eventType, handler);
  }

  removeLocalHandler(eventType, handlerName, handler) {
    // The OverlayViewGraphic does not care about the handlerName.
    this.overlayView.clickDiv.removeEventListener(eventType, handler);
  }

  addHandler(eventType, handlerName, handler) {
    super.addHandler(eventType, handlerName, handler);
  }

  /** @param {boolean} enabled */
  setPointerEvents(enabled) {
    this.overlayView.setPointerEvents(enabled);
  }

  /**
   * Sets whether this graphic can be clicked.
   * @param {boolean} clickable If `true`, this overlay will be clickable on the map; otherwise it will not.
   */
  setClickable(clickable) {
    EventHandlers.setClickable(clickable, this.overlayView);
    this._clickable = clickable;
  }

  isClickable() {
    return this._clickable;
  }

  /**
   * Attaches the graphic to a map or removes it from a map.
   * @param {google.maps.Map|null} map
   * The map to attach this graphic to, or `null` to detach the graphic from its current map.
   */
  setMap(map) {
    this.overlayView.setMap(map);
  }

  /**
   * sets the editing state of the image graphic
   * @param {boolean} editing true to enter edit mode, false to exit edit mode
   * @param {Object} _context context of the set editing method
   */
  async setEditing(editing, _context) {
    const map = MapService.getMap();

    if (editing) {
      // create editable image overlay
      this.editImageOverlay = new EditImageOverlay(this);
      this.editImageOverlay.setMap(map);
      // hide normal image overlay
      this.overlayView.setMap(null);
      this.editImageOverlay.addListener('overlay_view_changed', (event) => {
        // if the event has bounds, convert from latlng bounds to north, east, south, west
        if (event.bounds) {
          const latLngBounds = event.bounds;
          const ne = latLngBounds.getNorthEast();
          const sw = latLngBounds.getSouthWest();
          this.bounds = {
            north: ne.lat(),
            east: ne.lng(),
            south: sw.lat(),
            west: sw.lng(),
          };
        }
        // if the event has a rotation, update the rotation
        if (event.rotation) {
          this.rotation = event.rotation;
        }
        // update overlayview with the latlng bounds and updated rotation
        this.overlayView.setOptions({
          bounds: event.bounds,
          rotation: this.rotation,
        });
        this.subscriptions.publish('graphic_changed');
      });
      // open the info window
      this.subscriptions.publish('open_info_window', {
        toolbar: this.getToolbarOptions(),
        featureType: 'png',
      });
    } else {
      this.overlayView.setMap(map);
      this.editImageOverlay.setMap(null);
    }
    return false;
  }

  /**
   * subscribes to an event
   * @param {string} eventName the name of the event
   * @param {(arg0: SubscriptionEvent) => any} callback the callback function to execute when the event is published
   * @return {SubscriptionResult} An object with functions to alter the subscription.
   */
  subscribe(eventName, callback) {
    return this.subscriptions.addSubscription(eventName, callback);
  }

  /**
   * Gets the toolbar options for an image graphic.
   * @return {Object} the toolbar options for an image graphic
   */
  getToolbarOptions() {
    // Currently we return an empty array because the default toolbar options are set elsewhere.
    // The delete toolbar option is set where we create feature info window content
    // The pencil edit icon is set by the FeatureInfoWindow so that it can manage edit mode there.
    return {
      toolbarOptions: [],
    };
  }

  /**
   * Sets the graphic's styling options.
   * @param {boolean} highlighted The state of the graphic.
   */
  setHighlight(highlighted) {
    this.overlayView.highlighted = highlighted;
    this.overlayView.draw(); // force a redraw to get the highlight without waiting for a map move
  }

  toJSON() {
    const superJSON = super.toJSON();
    const latLngBounds = this.overlayView.bounds;
    const ne = latLngBounds.getNorthEast();
    const sw = latLngBounds.getSouthWest();
    const bounds = {
      north: ne.lat(),
      east: ne.lng(),
      south: sw.lat(),
      west: sw.lng(),
    };
    return {
      ...superJSON,
      bounds,
      graphicData: {
        bounds,
        featureType: 'png', // This appears to always be 'png' at present even if the image extension isn't .png
        imagePath: this.imagePath,
        rotation: this.rotation,
        preventScaling: this.preventScaling,
      },
    };
  }

  /**
   * @param {number} index
   */
  setZIndex(index) {
    this.overlayView.setZIndex(index);
  }

  getLocation() {
    const latLngBounds = this.overlayView.bounds;
    const ne = latLngBounds.getNorthEast();
    const sw = latLngBounds.getSouthWest();
    return {lat: ne.lat(), lng: (ne.lng() + sw.lng()) / 2};
  }
}

export {ImageGraphic};
